import _typeof from "@babel/runtime/helpers/esm/typeof";
import { MONITOR_TYPE } from "../constants";
import { isAppx, isObject } from "../utils";

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/;
var reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (Array.isArray(value)) {
    return false;
  }
  var type = _typeof(value);
  if (type === 'number' || type === 'boolean' || value === null) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) || object !== null && value in Object(object);
}
var charCodeOfDot = '.'.charCodeAt(0);
var reEscapeChar = /\\(\\)?/g;
var rePropName = RegExp(
// Match anything that isn't a dot or bracket.
'[^.[\\]]+' + '|' +
// Or match property names within brackets.
'\\[(?:' +
// Match a non-string expression.
'([^"\'][^[]*)' + '|' +
// Or match strings (supports escaping characters).
'(["\'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2' + ')\\]' + '|' +
// Or match "" as the space between consecutive dots or empty brackets.
'(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))', 'g');

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = function stringToPath(string) {
  var result = [];
  if (string.charCodeAt(0) === charCodeOfDot) {
    result.push('');
  }
  string.replace(rePropName, function (match, expression, quote, subString) {
    var key = match;
    if (quote) {
      key = subString.replace(reEscapeChar, '$1');
    } else if (expression) {
      key = expression.trim();
    }
    result.push(key);
    return '';
  });
  return result;
};
function castPath(value, object) {
  if (Array.isArray(value)) {
    return value;
  }
  return isKey(value, object) ? [value] : stringToPath(value);
}
var INFINITY = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value === 'string') {
    return value;
  }
  var result = "".concat(value);
  return result === '0' && 1 / value === -INFINITY ? '-0' : result;
}
function baseGet(object, path) {
  var pathList = castPath(path, object);
  var index = 0;
  var length = pathList.length;
  while (object !== null && isObject(object) && index < length) {
    // eslint-disable-next-line no-param-reassign
    object = object[toKey(pathList[index++])];
  }
  return index && index === length ? object : undefined;
}
function get(object, path, defaultValue) {
  var result = object === null ? undefined : baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

/**
 * 检测类型
 */
var toString = Object.prototype.toString;
var is = function is(type) {
  return function (obj) {
    return toString.call(obj) === "[object ".concat(type, "]");
  };
};
export var isRegExp = is('RegExp');
export var isString = is('String');
export var isFunction = is('Function');
export var isArray = is('Array');
function formatMessage(msg) {
  var cList = [];
  if (msg) {
    if (typeof msg === 'string') {
      cList.push(msg);
    } else if (isArray(msg)) {
      msg.forEach(function (item) {
        cList = cList.concat(formatMessage(item));
      });
    } else if (isObject(msg)) {
      if (msg instanceof Error) {
        cList.push(msg.toString());
      } else if (get(msg, 'errorMessage') || get(msg, 'errorCode')) {
        cList.push("errorMessage:".concat(get(msg, 'errorMessage', ''), "|errorCode:").concat(get(msg, 'errorCode', ''), "|traceId:").concat(get(msg, 'traceId', '')));
      } else {
        cList.push(JSON.stringify(msg));
      }
    } else {
      cList.push(msg + '');
    }
  }
  return cList;
}
var checkReportPromise = null;

/**
 * 外网环境或者内网非prod上报
 */
function checkReport() {
  if (checkReportPromise) return checkReportPromise;
  checkReportPromise = new Promise(function (resolve) {
    resolve(true);
  });
  return checkReportPromise;
}

/**
 * 上报业务错误
 * @param title  错误title
 * @param msg  错误详情
 */
function log(title, msg) {
  var content = {
    code: MONITOR_TYPE.LOG,
    msg: title
  };
  var cList = [];
  cList = formatMessage(msg);
  if (cList.length > 8) {
    console.warn('exceed eight point');
  }
  cList.forEach(function (item, index) {
    if (index > 7) return false;
    content["c".concat(index + 1)] = item;
  });
  return content;
}

/**
 * 获取当前页面path
 */
function getPath() {
  if (typeof window !== 'undefined') {
    var _window, _window$location;
    return (_window = window) === null || _window === void 0 ? void 0 : (_window$location = _window.location) === null || _window$location === void 0 ? void 0 : _window$location.href;
  }
  if (isAppx) {
    var url = '';
    try {
      var length = getCurrentPages().length;
      url = getCurrentPages()[length - 1].__proto__.route;
    } catch (e) {
      url = '';
    }
    return url;
  }
  return '';
}

/**
 * format扩展信息
 * @param extra
 */
function formatExtra(extra) {
  var extraObj = {};
  if (Array.isArray(extra)) {
    extra.forEach(function (item, index) {
      extraObj["c".concat(index + 2)] = item;
    });
  } else if (isObject(extra)) {
    // 删除路径别名，减少上报长度
    delete extra.userPath;
    return Object.assign(extraObj, extra);
  } else if (typeof extra === 'undefined') {
    return extraObj;
  } else {
    extraObj.c2 = extra;
  }
  return extraObj;
}

/**
 * 获取时区
 * @returns 时区
 * @example 东八区：UTC+8
 */
function getTimeZone() {
  var offset = new Date().getTimezoneOffset();
  var absOffset = Math.abs(offset / 60);
  return offset > 0 ? "UTC-".concat(absOffset) : "UTC+".concat(absOffset);
}
export { log, getPath, checkReport, formatMessage, formatExtra, get, getTimeZone };